import { Component, OnInit, OnDestroy } from '@angular/core';

import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../services/notification.service';
import { FileNavigationService } from '../../services/file-navigation.service';
import { NotificationEvent, ResourceType, NotificationAction, PermissionType } from '../../models/notification.model';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './notification-bell.html',
  styleUrls: ['./notification-bell.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  notifications: NotificationEvent[] = [];
  unreadCount: number = 0;
  isDropdownOpen: boolean = false;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isBellRinging: boolean = false; // Track ringing animation state
  
  // Pagination properties
  private currentPage: number = 1;
  private pageSize: number = 10;
  private totalNotifications: number = 0;
  hasMoreNotifications: boolean = false;
  
  // Expose enums to template
  ResourceType = ResourceType;
  NotificationAction = NotificationAction;

  private destroy$ = new Subject<void>();
  private previousNotificationCount: number = 0;

  constructor(
    private notificationService: NotificationService,
    private fileNavigationService: FileNavigationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Subscribe to notifications
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        // Trigger bell ring animation if new notifications arrived
        if (notifications.length > this.previousNotificationCount) {
          this.triggerBellAnimation();
        }
        
        this.previousNotificationCount = notifications.length;
        this.notifications = notifications;
      });

    // Subscribe to unread count
    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadCount = count;
      });

    // Initial load
    this.loadNotifications();
  }

  /**
   * Load notifications from API
   */
  loadNotifications(): void {
    this.isLoading = true;
    this.currentPage = 1;
    this.notificationService.getNotifications(this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.totalNotifications = response.data.totalCount || 0;
          this.hasMoreNotifications = (this.currentPage * this.pageSize) < this.totalNotifications;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load notifications:', err);
        this.isLoading = false;
      }
    });
  }

  /**
   * Load more notifications when "Load More" button is clicked
   */
  loadMoreNotifications(): void {
    this.isLoadingMore = true;
    this.currentPage++;
    this.notificationService.getNotifications(this.currentPage, this.pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.totalNotifications = response.data.totalCount || 0;
          this.hasMoreNotifications = (this.currentPage * this.pageSize) < this.totalNotifications;
        }
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error('Failed to load more notifications:', err);
        this.isLoadingMore = false;
        this.currentPage--; // Revert page number on error
      }
    });
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    
    // Load fresh notifications when opening dropdown (unless already loading)
    if (this.isDropdownOpen && !this.isLoading) {
      this.loadNotifications();
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  /**
   * Trigger bell ringing animation when new notification arrives
   * Adds ringing state, plays animation, then removes state
   */
  private triggerBellAnimation(): void {
    this.isBellRinging = true;
    
    // Remove animation after 600ms (duration of bellRing animation)
    setTimeout(() => {
      this.isBellRinging = false;
    }, 600);
  }

  /**
   * Mark single notification as read
   */
  markAsRead(notification: NotificationEvent): void {
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification.id).subscribe({
        error: (err) => {
          console.error('Failed to mark notification as read:', err);
        }
      });
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      error: (err) => {
        console.error('Failed to mark all as read:', err);
      }
    });
  }

  /**
   * Delete notification
   */
  deleteNotification(id: number): void {
    this.notificationService.deleteNotification(id).subscribe({
      error: (err) => {
        console.error('Failed to delete notification:', err);
      }
    });
  }

  /**
   * Navigate to file from notification
   * Extracts fileId from notification metadata and navigates to the file
   */
  navigateToFile(notification: NotificationEvent): void {
    // Only navigate for file permission notifications
    if (notification.resourceType !== ResourceType.File) {
      console.warn('Navigation only supported for file notifications');
      return;
    }

    // Extract fileId from metadata
    let fileId: number | null = null;
    if (notification.metadata) {
      try {
        // Try parsing as number first
        fileId = parseInt(notification.metadata, 10);
        if (isNaN(fileId)) {
          // Try parsing as JSON
          const parsed = JSON.parse(notification.metadata);
          fileId = parsed.fileId || parsed.id || parsed.file?.id;
        }
      } catch (e) {
        console.error('Failed to parse metadata:', notification.metadata, e);
        return;
      }
    }

    if (!fileId || fileId <= 0) {
      console.error('Invalid or missing fileId in notification metadata:', {
        notification,
        extractedFileId: fileId,
        metadata: notification.metadata
      });
      return;
    }

    console.log('Navigating to file:', { fileId, notification });

    // Close dropdown
    this.closeDropdown();

    // Mark notification as read first
    this.markAsRead(notification);

    // Navigate to file-management page
    this.router.navigate(['/file-management']).then(() => {
      console.log('Navigated to file-management, triggering file navigation:', fileId);
      // Trigger file navigation after route change
      this.fileNavigationService.navigateToFile(fileId);
    }).catch((err) => {
      console.error('Navigation failed:', err);
    });
  }

  /**
   * Get notification message based on type and action
   */
  getNotificationMessage(notification: NotificationEvent): string {
    const resourceName = notification.resourceName || 'Resource';

    switch (notification.resourceType) {
      case ResourceType.File:
        return this.getFileNotificationMessage(notification, resourceName);
      case ResourceType.Service:
        return this.getServiceNotificationMessage(notification, resourceName);
      default:
        return resourceName;
    }
  }

  /**
   * Get file permission notification message
   */
  private getFileNotificationMessage(notification: NotificationEvent, resourceName: string): string {
    const action = notification.action || NotificationAction.Granted;

    switch (action) {
      case NotificationAction.Granted:
        return `permission granted for "${resourceName}"`;
      case NotificationAction.Revoked:
        return `permission revoked for "${resourceName}"`;
      case NotificationAction.Updated:
        return `permission updated for "${resourceName}"`;
      case NotificationAction.Restricted:
        return `access restricted for "${resourceName}"`;
      default:
        return `Permission change for "${resourceName}"`;
    }
  }

  /**
   * Get service notification message
   */
  private getServiceNotificationMessage(notification: NotificationEvent, resourceName: string): string {
    const action = notification.action || NotificationAction.Granted;

    switch (action) {
      case NotificationAction.Granted:
        return `permission granted for "${resourceName}" service`;
      case NotificationAction.Revoked:
        return `permission revoked for "${resourceName}" service`;
      case NotificationAction.Updated:
        return `permission updated for "${resourceName}" service`;
      case NotificationAction.Restricted:
        return `access restricted for "${resourceName}" service`;
      default:
        return `Permission change for "${resourceName}" service`;
    }
  }

  /**
   * View all notifications
   */
//   viewAllNotifications(): void {
//     this.closeDropdown();
//     this.router.navigate(['/notifications']);
//   }

  /**
   * Get relative time string
   */
  getRelativeTime(createdAt: string): string {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
